import { useMutation } from "@tanstack/react-query";
import { AxiosError, isAxiosError } from "axios";

import { copilotApi } from "src/api/endpoints";
import { AutoMappingRequest } from "src/clients/copilot/api";
import { useWorkspaceContext } from "src/router/routerContextHooks";

const queryKeys = {
  autoMapping: (baseUrl: string) => ["auto-mapping", baseUrl],
  fetchAutoMapping: (baseUrl: string) => ["fetch-auto-mapping", baseUrl],
};

export const useAutoMapping = () => {
  const { workspace } = useWorkspaceContext();

  return useMutation({
    mutationKey: queryKeys.autoMapping(workspace.base_url!),
    mutationFn: async (request: AutoMappingRequest) => {
      if (!workspace.base_url) {
        throw new Error("Workspace base URL is not set");
      }

      const response = await copilotApi(
        workspace.base_url,
      ).getAutoMappingSuggestionsCopilotApiV1AutoMappingPost({
        autoMappingRequest: request,
      });
      if (response.data) {
        return response.data;
      } else {
        throw new Error(response.statusText);
      }
    },
    retry: (failureCount: number, error: AxiosError) => {
      const errorCode = isAxiosError(error) && error.status;
      return errorCode === 424 && failureCount < 5;
    },
  });
};

export const useFetchAutoMapping = () => {
  const { workspace } = useWorkspaceContext();
  return useMutation({
    retryDelay: 3000,
    mutationKey: queryKeys.fetchAutoMapping(workspace.base_url!),
    mutationFn: async (mappingId: AutoMappingRequest["id"]) => {
      if (!workspace.base_url) {
        throw new Error("Workspace base URL is not set");
      }

      const response = await copilotApi(
        workspace.base_url,
      ).getAutoMappingSuggestionCopilotApiV1AutoMappingMappingIdGet({
        mappingId,
      });

      if (response.data) {
        return response.data;
      } else {
        throw new Error(response.statusText);
      }
    },
    retry: (failureCount: number, error: AxiosError) => {
      const shouldRetry = isAxiosError(error) && failureCount < 10;

      return shouldRetry;
    },
  });
};
